import React from "react";
import { useMutation, useQuery } from "react-query";
import Axios from "axios";
import {
  antdAsyncMessageError,
  antdAsyncMessageSuccess,
} from "../../../../../helpers/AsyncMessage";
import { Form, Input, Button, Checkbox, Select } from "antd";
import ModalSpecial from "../../../../../components/Layout/Modals/ModalSpecial";
import { debounce } from "lodash";

const fetchProducts = async (value) => {
  const response = await Axios.get(
    `/api/v1/products?page=1&per_page=30&q[title_or_identifier_i_cont_any]=${value}`
  );
  return response.data;
};

const ModalNewProduct = ({
  onClose,
  modalOpt,
  customSuppliers,
  refetchProducts,
  customProducts,
  supplierId,
}) => {
  const [form] = Form.useForm();
  const [value, setValue] = React.useState("");
  const initialLoad = React.useRef(true);
  const [isForceSale, setIsForceSale] = React.useState(false);

  const debouncedSetValue = React.useCallback(
    debounce((value) => setValue(value), 500),
    []
  );
  const {
    data: products,
    isLoading,
    error,
  } = useQuery(["products", value], () => fetchProducts(value));

  const mutationPOST = useMutation({
    mutationFn: (value) => {
      return Axios.post("api/v1/product_suppliers/", {
        product_supplier: {
          supplier_id: supplierId,
          code: value.code,
          product_id: value.product_id !== "" ? Number(value.product_id) : "",
          quantity: String(value?.quantity),
          wholesale_price: String(value.wholesale_price),
          category: value.category,
          force_sale: isForceSale,
        },
      }).then((res) => res.data);
    },
    onSuccess: () => {
      antdAsyncMessageSuccess({ text: "Successfully created", key: "default" });
      refetchProducts();
      onClose();
    },
    onError: antdAsyncMessageError,
  });

  const mutationPUT = useMutation({
    mutationFn: (value) => {
      return Axios.put(`api/v1/product_suppliers/${modalOpt.record_id}`, {
        product_supplier: {
          supplier_id: supplierId,
          code: value.code,
          product_id: value.product_id,
          quantity: value?.quantity,
          wholesale_price: value.wholesale_price,
          category: value.category,
          force_sale: isForceSale,
        },
      }).then((res) => res.data);
    },
    onSuccess: () => {
      antdAsyncMessageSuccess({ text: "Successfully updated", key: "default" });
      refetchProducts();
      onClose();
    },
    onError: antdAsyncMessageError,
  });

  React.useEffect(() => {
    if (modalOpt.type === "edit_product") {
      const productToEdit = customProducts?.data?.find(
        (product) => product.id === modalOpt.record_id
      );
      if (initialLoad.current) {
        setValue(productToEdit?.sku);
        initialLoad.current = false;
      }
      const productInProductsData = products?.data?.find(
        (product) => product.identifier === productToEdit.sku
      );
      if (productToEdit) {
        form.setFieldsValue({
          quantity: productToEdit.quantity || undefined,
          wholesale_price: productToEdit.wholesale_price || undefined,
          code: productToEdit.code || undefined,
          category: productToEdit.category || undefined,
        });
        setIsForceSale(productToEdit.force_sale);
      }
      if (productInProductsData) {
        form.setFieldsValue({
          product_id: productInProductsData.id || undefined,
          supplier_id: supplierId || undefined,
        });
      }
    } else {
      form.setFieldsValue({
        supplier_id: modalOpt.record_id,
      });
    }
  }, [modalOpt, form, customSuppliers, products]);

  return (
    <ModalSpecial
      style={{ width: 300 }}
      text={
        <span className="font-bold text-xl text-black">
          {modalOpt.type === "edit_product" ? "Edit" : "Add"} SKU
        </span>
      }
      onClose={onClose}
    >
      <Form
        form={form}
        layout="vertical"
        name="basic"
        onFinish={(values) => {
          if (modalOpt.type === "edit_product") {
            mutationPUT.mutate(values);
          } else {
            mutationPOST.mutate(values);
          }
        }}
        autoComplete="off"
      >
        <Form.Item
          label="SKU"
          name="product_id"
          rules={[{ required: true, message: "Can't be empty" }]}
        >
          <Select
            placeholder="Select a SKU"
            showSearch
            onSearch={debouncedSetValue}
            loading={isLoading}
            filterOption={false}
          >
            {isLoading ? (
              <Select.Option>Loading...</Select.Option>
            ) : error ? (
              <Select.Option>Error loading products</Select.Option>
            ) : (
              products?.data?.map((product) =>
                product.id !== null ? (
                  <Select.Option key={product.id} value={product.id}>
                    {product.title}
                  </Select.Option>
                ) : null
              )
            )}
          </Select>
        </Form.Item>
        <Form.Item
          label="Supplier code"
          name="code"
          rules={[{ required: true, message: "Can't be empty" }]}
        >
          <Input placeholder="Type a supplier code" />
        </Form.Item>
        <Form.Item
          label="Supplier category"
          name="category"
          rules={[{ required: true, message: "Can't be empty" }]}
        >
          <Input placeholder="Type a supplier category" />
        </Form.Item>
        <Form.Item
          label="Wholesale Price"
          name="wholesale_price"
          rules={[{ required: true, message: "Can't be empty" }]}
        >
          <Input
            type="number"
            placeholder="Type in"
            suffix={<p style={{ margin: 0 }}>€</p>}
          />
        </Form.Item>
        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[{ required: true, message: "Can't be empty" }]}
        >
          <Input type="number" placeholder="Type in" />
        </Form.Item>
        <Form.Item label="Force sale" name="force_sale">
          <Checkbox
            onClick={() => setIsForceSale((prevState) => !prevState)}
            checked={isForceSale}
          >
            Force sale
          </Checkbox>
        </Form.Item>
        <Form.Item>
          <Button
            className="w-100"
            type="primary"
            htmlType="submit"
            loading={
              modalOpt.type === "edit_product"
                ? mutationPUT.isLoading
                : mutationPOST.isLoading
            }
          >
            {modalOpt.type === "edit_product" ? "Save" : "Add"}
          </Button>
        </Form.Item>
      </Form>
    </ModalSpecial>
  );
};

export default ModalNewProduct;
